import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type Campaign } from '@hooks-api/types/dataModel/campaigns'
import { useEffect, useRef, type SetStateAction } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { BUTLER_TAB } from '../constants/butler-tab'
import * as cartService from '../core/cartService'
import { type FpB2bCartValues, type PartialCartItem } from '../types'
import { validateCartItem } from '../utils/validateCartItem'

export const STM_CART_STORAGE_KEY = 'stmCartStorage'

type StmCartState = Partial<Record<Campaign['campaign_id'], FpB2bCartValues>>

export const defaultStmCartValues: FpB2bCartValues = {
  butler: {
    draftName: '',
    orderId: null,
    isImported: false,
    errors: {},
    selectedTab: BUTLER_TAB.FILE,
    items: [],
    fileName: '',
  },
  cart: {
    items: [],
    addonItems: [],
    specificAddonItems: [],
  },
  merchant: {
    index: null,
    billingFirstName: '',
    billingCountry: null,
  },
  coupon: {
    manualInputCouponIds: [],
    selectedManualCouponId: null,
    selectedRegularGiveaways: [],
    selectedGiveaways: [],
    redeemedCodes: [],
    hasApplyManuallyRedeemedCodes: false,
    selectedClaimCouponId: null,
    // 點數兌換券僅 WP 購物車消費；FP/STM 不讀此欄位，補型別預設即可，無需 persist version migration。
    selectedExchangeVoucherRedeemCodes: [],
    selectedExchangeVoucherGiveaways: [],
  },
  shipping: {
    appliedShippingMethodId: null,
  },
  paymentTransaction: {
    id: null,
    status: null,
    appliedRepaymentMethodId: null,
    orderHash: null,
  },
}

export const useStmCartStoreBase = create<StmCartState>()(
  persist(
    () => ({}),
    {
      name: STM_CART_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 0,
    },
  ),
)

const registerUpdateStore = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  // 當使用者切換頁面時，重新hydrate store
  const updateStore = () => {
    useStmCartStoreBase.persist.rehydrate()
  }

  document.addEventListener('visibilitychange', updateStore)
  window.addEventListener('focus', updateStore)
}
registerUpdateStore()

export const getStmCartValues = (campaignId: number | null): FpB2bCartValues =>
  (campaignId && useStmCartStoreBase.getState()[campaignId]) || defaultStmCartValues

export interface StmCartItemsEntry {
  campaignId: number
  items: PartialCartItem[]
}

/** 給 hook 以外的流程一次寫多個品牌購物車用（CSV 快速下單提交成功後）；一次 setState，persist 只序列化一回 */
export const upsertStmCartItemsForCampaigns = (entries: StmCartItemsEntry[]): void => {
  useStmCartStoreBase.setState(prevState => entries.reduce((nextState, { campaignId, items }) => {
    const campaignValues = nextState[campaignId] || defaultStmCartValues
    const cart = items.filter(validateCartItem).reduce((nextCart, item) => cartService.upsertCartItem(nextCart, item), campaignValues.cart)
    return { ...nextState, [campaignId]: { ...campaignValues, cart } }
  }, prevState))
}

export const useStmCartSelector = <U>(campaignId: number | null, selector: (state: FpB2bCartValues) => U) => {
  return useStmCartStoreBase(useShallow(state => selector((campaignId && state[campaignId]) || defaultStmCartValues)))
}

export type SetFpB2bCartValuesCallback = (state: FpB2bCartValues) => void

export const useStmCartStore = (campaignId: number | null) => {
  if (!campaignId) {
    throw new Error('campaignId is required')
  }
  const cartValues = useStmCartStoreBase(state => state[campaignId] || defaultStmCartValues)
  const callbacks = useRef<SetFpB2bCartValuesCallback[]>([])

  const setState = useCallbackEvent((value: SetStateAction<FpB2bCartValues>, callback?: SetFpB2bCartValuesCallback) => {
    const prevState = useStmCartStoreBase.getState()[campaignId]
    useStmCartStoreBase.setState((prevState) => {
      const nextState = typeof value === 'function' ? value(prevState[campaignId] || defaultStmCartValues) : value
      return {
        ...prevState,
        [campaignId]: nextState,
      }
    })
    const nextState = useStmCartStoreBase.getState()[campaignId]
    if (callback) callbacks.current.push(callback)
    return { prevState, nextState }
  })

  useEffect(() => {
    if (callbacks.current) {
      callbacks.current.forEach(func => func(cartValues))

      callbacks.current = []
    }
  }, [cartValues])

  return [cartValues, setState] as const
}
